import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Family {
  id: number;
  join_date: string | null;
  status: string | null;
  last_name: string;
  first_name: string;
  combined_name: string;
  address: string | null;
  email: string;
  phone_number: string | null;
  created_at: string;
  updated_at: string;
}

interface FamilyAvailability {
  id: number;
  family_id: number;
  semester_id: number;
  semester_name: string;
  max_students: number;
  preferences_notes: string | null;
  created_at: string;
  updated_at: string;
}

interface StudentAssignment {
  id: number;
  student_id: number;
  student_name: string;
  semester_name: string;
  semester_id: number;
  family_email_sent_at: string | null;
  student_email_sent_at: string | null;
  created_at: string;
}

interface FamilyModalProps {
  familyId: number;
  isOpen: boolean;
  onClose: () => void;
  onFamilyUpdated?: (family: Family) => void;
}

export const FamilyModal: React.FC<FamilyModalProps> = ({
  familyId,
  isOpen,
  onClose,
  onFamilyUpdated
}) => {
  const [family, setFamily] = useState<Family | null>(null);
  const [editedFamily, setEditedFamily] = useState<Family | null>(null);
  const [availability, setAvailability] = useState<FamilyAvailability[]>([]);
  const [assignments, setAssignments] = useState<StudentAssignment[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && familyId) {
      loadFamilyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, familyId]);

  const loadFamilyData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load family details
      const { data: familyData, error: familyError } = await supabase
        .from('families')
        .select('*')
        .eq('id', familyId)
        .single();

      if (familyError) throw familyError;
      
      setFamily(familyData);
      setEditedFamily({ ...familyData });

      // Load family availability across semesters
      const { data: availabilityData, error: availabilityError } = await supabase
        .from('family_availability')
        .select('*')
        .eq('family_id', familyId)
        .order('semester_id', { ascending: false });

      if (availabilityError) throw availabilityError;

      // Load semester names separately
      const semesterIds = availabilityData?.map(a => a.semester_id).filter(Boolean) || [];
      let semesterNames: Record<number, string> = {};
      
      if (semesterIds.length > 0) {
        const { data: semesterData } = await supabase
          .from('semesters')
          .select('id, name')
          .in('id', semesterIds);
        
        semesterNames = semesterData?.reduce((acc, semester) => ({
          ...acc,
          [semester.id]: semester.name
        }), {}) || {};
      }

      const formattedAvailability = availabilityData?.map(a => ({
        ...a,
        semester_name: semesterNames[a.semester_id] || 'Unknown Semester'
      })) || [];

      setAvailability(formattedAvailability);

      // Load student assignments history
      const { data: assignmentData, error: assignmentError } = await supabase
        .from('assignments')
        .select('id, student_id, semester_id, family_email_sent_at, student_email_sent_at, created_at')
        .eq('family_id', familyId)
        .order('created_at', { ascending: false });

      if (assignmentError) throw assignmentError;

      // Load student names separately
      const studentIds = assignmentData?.map(a => a.student_id).filter(Boolean) || [];
      let studentNames: Record<number, string> = {};
      
      if (studentIds.length > 0) {
        const { data: studentData } = await supabase
          .from('students')
          .select('id, combined_name')
          .in('id', studentIds);
        
        studentNames = studentData?.reduce((acc, student) => ({
          ...acc,
          [student.id]: student.combined_name
        }), {}) || {};
      }

      // Load semester names for assignments  
      const assignmentSemesterIds = assignmentData?.map(a => a.semester_id).filter(Boolean) || [];
      let assignmentSemesterNames: Record<number, string> = {};
      
      if (assignmentSemesterIds.length > 0) {
        const { data: assignmentSemesterData } = await supabase
          .from('semesters')
          .select('id, name')
          .in('id', assignmentSemesterIds);
        
        assignmentSemesterNames = assignmentSemesterData?.reduce((acc, semester) => ({
          ...acc,
          [semester.id]: semester.name
        }), {}) || {};
      }

      const formattedAssignments = assignmentData?.map(a => ({
        ...a,
        student_name: studentNames[a.student_id] || 'Unknown Student',
        semester_name: assignmentSemesterNames[a.semester_id] || 'Unknown Semester'
      })) || [];

      setAssignments(formattedAssignments); // Update assignments
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!editedFamily?.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!editedFamily?.last_name?.trim()) {
      errors.last_name = 'Last name is required';
    }
    if (!editedFamily?.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedFamily.email)) {
      errors.email = 'Invalid email format';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!editedFamily || !validateForm()) return;

    setIsSaving(true);
    setError(null);

    try {
      // Update combined_name based on first and last names
      const updatedFamily = {
        ...editedFamily,
        combined_name: `${editedFamily.last_name}, ${editedFamily.first_name}`,
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('families')
        .update(updatedFamily)
        .eq('id', familyId)
        .select()
        .single();

      if (error) throw error;

      setFamily(data);
      setEditedFamily({ ...data });
      setIsEditing(false);

      if (onFamilyUpdated) {
        onFamilyUpdated(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save family');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (family) {
      setEditedFamily({ ...family });
    }
    setIsEditing(false);
    setValidationErrors({});
    setError(null);
  };

  const handleFieldChange = (field: keyof Family, value: any) => {
    if (!editedFamily) return;
    
    setEditedFamily({
      ...editedFamily,
      [field]: value
    });

    // Clear validation error when field is changed
    if (validationErrors[field]) {
      setValidationErrors({
        ...validationErrors,
        [field]: ''
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#2a2a2a',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #444',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#333'
        }}>
          <h2 style={{ color: 'white', margin: 0 }}>
            Family Information
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#ccc',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'white' }}>
              Loading family information...
            </div>
          ) : error ? (
            <div style={{
              padding: '20px',
              color: '#ff6b6b',
              backgroundColor: '#2d1b1b',
              border: '1px solid #ff6b6b',
              margin: '20px',
              borderRadius: '4px'
            }}>
              Error: {error}
            </div>
          ) : family ? (
            <div style={{ padding: '20px' }}>
              {/* Basic Information */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: 'white', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '8px' }}>
                  Basic Information
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px' }}>
                  {renderField('first_name', 'First Name', editedFamily?.first_name || '')}
                  {renderField('last_name', 'Last Name', editedFamily?.last_name || '')}
                  {renderField('email', 'Email', editedFamily?.email || '')}
                  {renderField('phone_number', 'Phone Number', editedFamily?.phone_number || '')}
                  {renderField('address', 'Address', editedFamily?.address || '', 'textarea')}
                  {renderSelectField('status', 'Status', editedFamily?.status || '', [
                    { value: '', label: 'Not specified' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Inactive', label: 'Inactive' },
                    { value: 'On Hold', label: 'On Hold' }
                  ])}
                </div>
                
                {editedFamily?.join_date && (
                  <div style={{ marginTop: '15px' }}>
                    {renderField('join_date', 'Join Date', editedFamily.join_date.split('T')[0], 'date')}
                  </div>
                )}
              </div>

              {/* Availability Settings */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ color: 'white', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '8px' }}>
                  Availability Settings ({availability.length} semesters)
                </h3>
                
                {availability.length === 0 ? (
                  <p style={{ color: '#ccc', fontStyle: 'italic' }}>
                    No availability settings found.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {availability.map(avail => (
                      <div key={avail.id} style={{
                        backgroundColor: '#333',
                        padding: '15px',
                        borderRadius: '6px',
                        border: '1px solid #555'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'white', fontWeight: 'bold', marginBottom: '8px' }}>
                              {avail.semester_name}
                            </div>
                            <div style={{ color: '#ccc', fontSize: '0.9em', marginBottom: '5px' }}>
                              Max Students: <span style={{ color: '#28a745', fontWeight: 'bold' }}>{avail.max_students}</span>
                            </div>
                            {avail.preferences_notes && (
                              <div style={{ color: '#ccc', fontSize: '0.9em' }}>
                                Notes: {avail.preferences_notes}
                              </div>
                            )}
                          </div>
                          <div style={{ color: '#888', fontSize: '0.8em', textAlign: 'right' }}>
                            Updated: {new Date(avail.updated_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Hosted Students History */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ color: 'white', marginBottom: '20px', borderBottom: '2px solid #28a745', paddingBottom: '8px' }}>
                  Hosted Students History ({assignments.length} assignments)
                </h3>
                
                {assignments.length === 0 ? (
                  <p style={{ color: '#ccc', fontStyle: 'italic' }}>
                    No student assignments found.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {assignments.map(assignment => (
                      <div key={assignment.id} style={{
                        backgroundColor: '#333',
                        padding: '15px',
                        borderRadius: '6px',
                        border: '1px solid #555'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div>
                            <div style={{ color: 'white', fontWeight: 'bold', marginBottom: '5px' }}>
                              {assignment.student_name}
                            </div>
                            <div style={{ color: '#ccc', fontSize: '0.9em' }}>
                              Semester: {assignment.semester_name}
                            </div>
                            <div style={{ color: '#ccc', fontSize: '0.9em' }}>
                              Assigned: {new Date(assignment.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', fontSize: '0.8em' }}>
                            <div style={{ color: assignment.student_email_sent_at ? '#28a745' : '#6c757d' }}>
                              Student Email: {assignment.student_email_sent_at ? '✓ Sent' : '○ Not sent'}
                            </div>
                            <div style={{ color: assignment.family_email_sent_at ? '#28a745' : '#6c757d' }}>
                              Family Email: {assignment.family_email_sent_at ? '✓ Sent' : '○ Not sent'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {family && (
          <div style={{
            padding: '20px',
            borderTop: '1px solid #444',
            backgroundColor: '#333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ color: '#ccc', fontSize: '0.9em' }}>
              Created: {new Date(family.created_at).toLocaleDateString()} | 
              Updated: {new Date(family.updated_at).toLocaleDateString()}
            </div>
            
            <div style={{ display: 'flex', gap: '10px' }}>
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isSaving ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.7 : 1
                    }}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Edit Family
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function renderField(field: keyof Family, label: string, value: string, type: 'input' | 'textarea' | 'date' = 'input') {
    const hasError = validationErrors[field];
    
    return (
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', color: 'white', marginBottom: '5px', fontWeight: 'bold' }}>
          {label}:
        </label>
        {isEditing ? (
          <>
            {type === 'textarea' ? (
              <textarea
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: hasError ? '1px solid #ff6b6b' : '1px solid #666',
                  borderRadius: '4px',
                  fontSize: '0.9em',
                  resize: 'vertical'
                }}
              />
            ) : (
              <input
                type={type === 'date' ? 'date' : 'text'}
                value={value}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: '#444',
                  color: 'white',
                  border: hasError ? '1px solid #ff6b6b' : '1px solid #666',
                  borderRadius: '4px',
                  fontSize: '0.9em'
                }}
              />
            )}
            {hasError && (
              <div style={{ color: '#ff6b6b', fontSize: '0.8em', marginTop: '4px' }}>
                {hasError}
              </div>
            )}
          </>
        ) : (
          <div style={{ 
            color: '#ccc', 
            padding: '8px',
            backgroundColor: '#1a1a1a',
            borderRadius: '4px',
            minHeight: type === 'textarea' ? '60px' : 'auto'
          }}>
            {value ? (type === 'date' ? new Date(value).toLocaleDateString() : value) : <em style={{ opacity: 0.6 }}>Not specified</em>}
          </div>
        )}
      </div>
    );
  }

  function renderSelectField(field: keyof Family, label: string, value: string, options: Array<{value: string, label: string}>) {
    return (
      <div style={{ marginBottom: '10px' }}>
        <label style={{ display: 'block', color: 'white', marginBottom: '5px', fontWeight: 'bold' }}>
          {label}:
        </label>
        {isEditing ? (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field, e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: '#444',
              color: 'white',
              border: '1px solid #666',
              borderRadius: '4px',
              fontSize: '0.9em'
            }}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <div style={{ 
            color: '#ccc', 
            padding: '8px',
            backgroundColor: '#1a1a1a',
            borderRadius: '4px'
          }}>
            {options.find(opt => opt.value === value)?.label || <em style={{ opacity: 0.6 }}>Not specified</em>}
          </div>
        )}
      </div>
    );
  }
};